/**
 * Authenticated Fetch Utility
 * 
 * Wraps the native fetch() to automatically attach the Supabase JWT
 * as an Authorization: Bearer header on all API requests.
 * Also includes x-user-id for backward compatibility during transition.
 */

import { supabase } from '../lib/supabase';

/**
 * Fetch wrapper that injects the Supabase session JWT into the request headers.
 * Use this instead of raw fetch() for all /api/* calls.
 * 
 * @example
 *   const res = await authFetch('/api/credits/balance');
 *   const data = await res.json();
 * 
 * @example
 *   const res = await authFetch('/api/openai/chat', {
 *     method: 'POST',
 *     body: JSON.stringify({ prompt: '...' }),
 *   });
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers);

  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.access_token) {
      headers.set('Authorization', `Bearer ${session.access_token}`);

      // Also send x-user-id for backward compatibility with routes
      // that haven't been migrated yet. Can be removed once all routes use req.userId.
      if (session.user?.id) {
        headers.set('x-user-id', session.user.id);
      }
      if (session.user?.email) {
        headers.set('x-user-email', session.user.email);
      }
    }
  } catch (err) {
    // If we can't get the session, proceed without auth headers
    console.warn('[authFetch] Failed to get session:', err);
  }

  // Ensure Content-Type is set for JSON bodies (unless already set or FormData)
  if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, { ...options, headers });
}

export default authFetch;
